import { useState, useEffect, useCallback, useRef, memo } from 'react';
import { io } from 'socket.io-client';
import CircleIcon from '@mui/icons-material/Circle';
import { green, red } from '@mui/material/colors';
import { queueAlarmSound } from '../alarms/alarmSounds';

const WS_BUFFER_MS = 1000;
const STALE_TIMEOUT_MS = 3 * 60 * 1000;

// Pede permissao para notificacoes ao carregar
if ('Notification' in window && Notification.permission === 'default') {
  Notification.requestPermission();
}

function Monitor() {
  const [notificationEnabled] = useState(
    Notification.permission === 'granted'
  );

  // Refs para bufferizar mensagens new_alarms
  const pendingAlarms = useRef([]);
  const pendingRemoved = useRef([]);
  const pendingPainel = useRef(null);
  const flushTimer = useRef(null);
  const staleTimer = useRef(null);
  const socketRef = useRef(null);
  const [connected, setConnected] = useState(false);

  // Le filtros do localStorage (salvos pelo Alarms.js)
  const getFilters = useCallback(() => {
    try {
      const saved = localStorage.getItem('alarmFilters')
      return saved ? JSON.parse(saved) : {}
    } catch {
      return {}
    }
  }, []);

  // Le configuracoes de notificacao do Notify.js
  const getNotifyOptions = useCallback(() => {
    try {
      const enabled = localStorage.getItem('notificationsEnabled')
      const options = localStorage.getItem('options')
      return {
        enabled: enabled ? JSON.parse(enabled) : true,
        options: options ? JSON.parse(options) : { sev3: true, sev4: true, sev5: true }
      }
    } catch {
      return { enabled: true, options: { sev3: true, sev4: true, sev5: true } }
    }
  }, []);

  const getSocketUrl = useCallback(() => {
    const url = process.env.REACT_APP_API_URL || '__BACKEND_API__';
    return url.replace(/\/+$/, '');  // origem http(s); Socket.IO faz upgrade pra ws
  }, []);

  // Aplica filtros do usuario nos alarmes recebidos
  const filterAlarms = useCallback((alarms) => {
    if (!alarms || alarms.length === 0) return [];

    const filters = getFilters();
    const notifyOpts = getNotifyOptions();

    return alarms.filter(alarm => {
      // Verifica se notificacoes estao habilitadas
      if (!notifyOpts.enabled) return false;

      // Filtro de severidade do Notify (sev3, sev4, sev5)
      const sev = alarm.severity;
      if (sev === 3 && !notifyOpts.options.sev3) return false;
      if (sev === 4 && !notifyOpts.options.sev4) return false;
      if (sev === 5 && !notifyOpts.options.sev5) return false;

      // Filtro por empresa (do Alarms.js)
      if (filters.cod_empresa && filters.cod_empresa.length > 0) {
        if (!filters.cod_empresa.includes(alarm.cod_empresa)) return false;
      }

      // Filtro por severidade (do Alarms.js)
      if (filters.severity && filters.severity.length > 0) {
        if (!filters.severity.includes(alarm.severity)) return false;
      }

      // Filtro por acks (0 = nao vistado, 1 = vistado)
      if (filters.acks && filters.acks.length > 0) {
        const hasAck = alarm.acks > 0;
        const wantNoAck = filters.acks.includes(0);
        const wantAck = filters.acks.some(a => a > 0);

        if (wantNoAck && hasAck) return false;
        if (wantAck && !hasAck) return false;
      }

      return true;
    });
  }, [getFilters, getNotifyOptions]);

  // Mostra notificacao desktop agrupada por empresa
  const showNotification = useCallback((filteredAlarms) => {
    if (!notificationEnabled || filteredAlarms.length === 0) return;

    // Agrupa por empresa
    const byEmpresa = {};
    filteredAlarms.forEach(alarm => {
      const cod = alarm.cod_empresa;
      if (!byEmpresa[cod]) byEmpresa[cod] = [];
      byEmpresa[cod].push(alarm);
    });

    const empresas = Object.keys(byEmpresa);
    const total = filteredAlarms.length;
    let title = '';
    let body = '';

    // Se 1-2 alarmes, mostra detalhado
    if (total <= 2) {
      title = `${total} novo${total > 1 ? 's' : ''} alarme${total > 1 ? 's' : ''}`;
      body = filteredAlarms.map(a => `${a.cod_empresa} - ${a.name}`).join('\n');
    } else {
      // 3+ alarmes: agrupa por empresa
      title = `${total} novos alarmes`;
      body = empresas.map(cod => `${cod} - ${byEmpresa[cod].length} alarme${byEmpresa[cod].length > 1 ? 's' : ''}`).join('\n');
    }

    // Cria notificacao
    new Notification(title, {
      body: body,
      icon: '/favicon.ico',
      tag: 'horus-alarms',
      renotify: true
    });
  }, [notificationEnabled]);

  // Stale-data safety net: se 3min sem new_alarms, faz bulk refresh
  const resetStaleTimer = useCallback(() => {
    if (staleTimer.current) clearTimeout(staleTimer.current);
    staleTimer.current = setTimeout(() => {
      window.dispatchEvent(new CustomEvent('alarms-refresh'));
    }, STALE_TIMEOUT_MS);
  }, []);

  // Flush: envia todos os alarmes acumulados de uma vez
  const flushPending = useCallback(() => {
    flushTimer.current = null;

    if (pendingAlarms.current.length > 0) {
      const allAlarms = pendingAlarms.current;
      pendingAlarms.current = [];

      // Notificacao desktop + som agrupados
      const filtered = filterAlarms(allAlarms);
      if (filtered.length > 0) {
        showNotification(filtered);
        const maxSev = Math.max(...filtered.map(a => a.severity));
        queueAlarmSound(maxSev, filtered.length);
      }

      window.dispatchEvent(new CustomEvent('alarms-new', { detail: allAlarms }));
    }

    if (pendingRemoved.current.length > 0) {
      const allRemoved = pendingRemoved.current;
      pendingRemoved.current = [];
      window.dispatchEvent(new CustomEvent('alarms-removed', { detail: allRemoved }));
    }

    if (pendingPainel.current !== null) {
      window.dispatchEvent(new CustomEvent('painel-update', { detail: pendingPainel.current }));
      pendingPainel.current = null;
    }
  }, [filterAlarms, showNotification]);

  // Conexao Socket.IO (websocket-only; reconnect e heartbeat sao da lib).
  useEffect(() => {
    const socket = io(getSocketUrl(), {
      path: '/api/v1/ws/socket.io',   // mesmo prefixo do WS antigo -> nginx ja roteia
      transports: ['websocket'],
      withCredentials: true,          // envia o cookie horus_token no handshake
      reconnection: true,
      reconnectionDelay: 2000,
      reconnectionDelayMax: 10000,
      reconnectionAttempts: Infinity,
    });
    socketRef.current = socket;

    socket.on('connect', () => { setConnected(true); resetStaleTimer(); });
    socket.on('disconnect', () => setConnected(false));

    // Resync de estado ao RECONECTAR: o socket volta sozinho, mas nao replica o que
    // passou no buraco. Um refetch REST fecha esse furo (a tabela faz replace total).
    // Usa o evento do manager (nao o 'connect'), pra nao refetchar a toa no 1o load.
    const onReconnect = () => window.dispatchEvent(new CustomEvent('alarms-refresh'));
    socket.io.on('reconnect', onReconnect);

    socket.on('message', (data) => {
      if (!data || typeof data !== 'object') return;

      // Baixa frequencia: dispatch imediato
      if (data.type === 'ack_update') {
        window.dispatchEvent(new CustomEvent('ack-update', {
          detail: { cod_empresa: data.cod_empresa, event_ids: data.event_ids }
        }));
      }

      if (data.type === 'field_update') {
        window.dispatchEvent(new CustomEvent('alarm-field-update', { detail: data }));
      }

      if (data.type === 'viewing_start' || data.type === 'viewing_stop') {
        window.dispatchEvent(new CustomEvent('viewing-update', { detail: data }));
      }

      // Alta frequencia: bufferizar e fazer flush
      if (data.type === 'new_alarms') {
        resetStaleTimer();
        if (data.alarms && data.alarms.length > 0) {
          pendingAlarms.current = pendingAlarms.current.concat(data.alarms);
        }
        if (data.removed && data.removed.length > 0) {
          pendingRemoved.current = pendingRemoved.current.concat(data.removed);
        }
        if (data.painel) {
          pendingPainel.current = data.painel;
        }
        if (!flushTimer.current) {
          flushTimer.current = setTimeout(flushPending, WS_BUFFER_MS);
        }
      }
    });

    return () => {
      socket.io.off('reconnect', onReconnect);
      socket.disconnect();
      socketRef.current = null;
      if (staleTimer.current) clearTimeout(staleTimer.current);
    };
  }, [getSocketUrl, flushPending, resetStaleTimer]);

  // Bridge: FormDialog envia viewing_start/stop via CustomEvent → Socket.IO
  useEffect(() => {
    const handler = (e) => {
      const s = socketRef.current;
      if (s && s.connected) s.emit('viewing', e.detail);
    };
    window.addEventListener('viewing-send', handler);
    return () => window.removeEventListener('viewing-send', handler);
  }, []);

  return (
    <CircleIcon
      fontSize='10'
      sx={{
        ml: 1,
        color: connected ? green[500] : red[900],
      }}
    />
  );
}

export default memo(Monitor);
